import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBrandBySlug } from '../data/brands';
import { ArrowLeft, Lightbulb, Eye, Megaphone, Store } from 'lucide-react';

// ============================================
// GLOBAL MOTION CONSTANTS
// ============================================
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASING_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
const DURATION = {
  fast: 320,
  normal: 650,
  slow: 800,
  atmosphere: 1200,
  heroEntrance: 450,
};
const STAGGER = {
  tight: 70,
  normal: 90,
  relaxed: 120,
};

// ============================================
// BACKGROUND LAYER CONSTANTS
// ============================================
const BACKGROUND = {
  blurredCard: {
    opacity: 0.50,
    blurDesktop: 55,
    blurMobile: 40,
    scale: 1.25,
  },
  darkOverlay: {
    opacity: 0.42,
  },
  tintOverlay: {
    opacity: 0.48,
  },
  ambientGlow: {
    opacity: 0.45,
  },
  glow: {
    opacity: 0.32,
    blur: 45,
  },
};

// ============================================
// REDUCED MOTION HOOK
// ============================================
const usePrefersReducedMotion = () => {
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mediaQuery.matches);

    const handler = (e) => setPrefersReduced(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReduced;
};

// ============================================
// SCROLL REVEAL HOOK (IntersectionObserver)
// ============================================
const useScrollReveal = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const prefersReduced = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReduced]);

  return [ref, isVisible];
};

// ============================================
// PARALLAX HOOK (disabled for performance)
// ============================================
const useParallax = () => {
  return 0; // Disabled for performance
};

// ============================================
// BRAND ATMOSPHERIC BACKGROUNDS
// ============================================
const BrandAtmosphere = ({ slug, tintColor, accentColor, glowColor }) => {
  const atmosphereStyles = {
    // Cloud Air - Dreamy floating clouds
    'cloud-air': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large blurred cloud shapes */}
        <div
          className="absolute"
          style={{
            width: '500px',
            height: '200px',
            left: '5%',
            top: '15%',
            background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 40%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(40px)',
          }}
        />
        <div
          className="absolute"
          style={{
            width: '400px',
            height: '180px',
            right: '10%',
            top: '25%',
            background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 45%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
          }}
        />
        <div
          className="absolute"
          style={{
            width: '600px',
            height: '250px',
            left: '20%',
            top: '50%',
            background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(139,181,208,0.15) 0%, rgba(139,181,208,0.05) 40%, transparent 65%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute"
          style={{
            width: '350px',
            height: '150px',
            right: '5%',
            top: '60%',
            background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(255,255,255,0.08) 0%, transparent 60%)',
            borderRadius: '50%',
            filter: 'blur(45px)',
          }}
        />
        <div
          className="absolute"
          style={{
            width: '450px',
            height: '200px',
            left: '-5%',
            top: '70%',
            background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(155,197,224,0.12) 0%, transparent 60%)',
            borderRadius: '50%',
            filter: 'blur(55px)',
          }}
        />
        {/* Sky gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, ${tintColor}30 0%, transparent 40%, ${tintColor}20 100%)`,
          }}
        />
      </div>
    ),

    // Hurley - Beachy surfer ocean vibe
    'hurley': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Ocean gradient base */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, ${tintColor}50 0%, #1a3a4a 30%, #2a4a5a 60%, ${tintColor}40 100%)`,
            opacity: 0.6,
          }}
        />
        {/* Wave layers */}
        <svg className="absolute w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="waveGrad1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0.3" />
              <stop offset="100%" stopColor={accentColor} stopOpacity="0.1" />
            </linearGradient>
            <linearGradient id="waveGrad2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#4a7a8a" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#4a7a8a" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          {/* Large back wave */}
          <path
            d="M-100,500 Q150,420 400,480 T900,450 T1400,500 L1400,900 L-100,900 Z"
            fill="url(#waveGrad2)"
          />
          {/* Middle wave */}
          <path
            d="M-100,550 Q200,480 500,540 T1000,500 T1400,560 L1400,900 L-100,900 Z"
            fill="url(#waveGrad1)"
          />
          {/* Front wave */}
          <path
            d="M-100,620 Q250,560 550,610 T1100,580 T1400,630 L1400,900 L-100,900 Z"
            fill={tintColor}
            opacity="0.3"
          />
        </svg>
        {/* Sun glow in corner */}
        <div
          className="absolute"
          style={{
            width: '400px',
            height: '400px',
            right: '-100px',
            top: '-100px',
            background: `radial-gradient(circle, ${accentColor}40 0%, ${accentColor}15 30%, transparent 60%)`,
            filter: 'blur(30px)',
          }}
        />
      </div>
    ),

    // Catherine Malandrino - Modern fashion editorial
    'catherine-malandrino': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Rich plum gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${tintColor}60 0%, #2a1a2a 40%, ${tintColor}40 70%, #1a0a1a 100%)`,
            opacity: 0.7,
          }}
        />
        {/* Fashion-forward diagonal shapes */}
        <svg className="absolute w-full h-full" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="fashionGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0.2" />
              <stop offset="100%" stopColor={glowColor} stopOpacity="0.05" />
            </linearGradient>
          </defs>
          {/* Elegant diagonal lines */}
          <line x1="0" y1="200" x2="400" y2="0" stroke={accentColor} strokeWidth="1" opacity="0.15" />
          <line x1="0" y1="400" x2="600" y2="0" stroke={accentColor} strokeWidth="0.5" opacity="0.1" />
          <line x1="600" y1="800" x2="1000" y2="300" stroke={accentColor} strokeWidth="1" opacity="0.12" />
          {/* Abstract fashion shapes */}
          <ellipse cx="800" cy="200" rx="200" ry="300" fill="url(#fashionGrad)" transform="rotate(-20 800 200)" />
          <ellipse cx="150" cy="600" rx="150" ry="250" fill="url(#fashionGrad)" transform="rotate(15 150 600)" />
        </svg>
        {/* Accent glow */}
        <div
          className="absolute"
          style={{
            width: '500px',
            height: '500px',
            left: '-150px',
            top: '20%',
            background: `radial-gradient(circle, ${accentColor}20 0%, transparent 50%)`,
            filter: 'blur(40px)',
          }}
        />
      </div>
    ),

    // Sunday Walks - Natural forest/park vibe
    'sunday-walks': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Nature gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, ${tintColor}40 0%, #3a4a32 40%, ${tintColor}30 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Organic leaf-like shapes */}
        <svg className="absolute w-full h-full opacity-20" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <ellipse cx="100" cy="300" rx="80" ry="150" fill={accentColor} transform="rotate(-30 100 300)" />
          <ellipse cx="900" cy="200" rx="60" ry="120" fill={accentColor} transform="rotate(25 900 200)" />
          <ellipse cx="200" cy="650" rx="100" ry="180" fill={glowColor} transform="rotate(-15 200 650)" />
          <ellipse cx="800" cy="550" rx="70" ry="140" fill={accentColor} transform="rotate(20 800 550)" />
          <circle cx="500" cy="400" r="200" fill={tintColor} opacity="0.3" />
        </svg>
        {/* Soft sunlight glow */}
        <div
          className="absolute"
          style={{
            width: '600px',
            height: '600px',
            right: '-200px',
            top: '-200px',
            background: `radial-gradient(circle, rgba(255,255,200,0.1) 0%, transparent 50%)`,
          }}
        />
      </div>
    ),

    // Serene Island - Tropical resort water
    'serene-island': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Tropical water gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, #2a4a4a 0%, ${tintColor}60 30%, #3a5a5a 60%, ${tintColor}40 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Water ripple effect */}
        <svg className="absolute w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="waterGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0.15" />
              <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Horizontal water lines */}
          <path d="M0,300 Q300,280 600,300 T1200,300" stroke={accentColor} strokeWidth="2" fill="none" opacity="0.2" />
          <path d="M0,380 Q350,350 700,380 T1200,380" stroke={accentColor} strokeWidth="1.5" fill="none" opacity="0.15" />
          <path d="M0,460 Q400,430 800,460 T1200,460" stroke={glowColor} strokeWidth="1" fill="none" opacity="0.12" />
          <path d="M0,540 Q300,520 600,540 T1200,540" stroke={accentColor} strokeWidth="0.5" fill="none" opacity="0.1" />
          {/* Palm shadow hint */}
          <ellipse cx="150" cy="200" rx="100" ry="150" fill={tintColor} opacity="0.2" transform="rotate(-10 150 200)" />
        </svg>
        {/* Warm tropical glow */}
        <div
          className="absolute"
          style={{
            width: '500px',
            height: '300px',
            left: '30%',
            top: '-50px',
            background: `radial-gradient(ellipse, rgba(255,200,150,0.1) 0%, transparent 60%)`,
          }}
        />
      </div>
    ),

    // Arctic Plunge - Icy winter frost
    'arctic-plunge': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Icy gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, #2a4a5a 0%, ${tintColor}50 40%, #3a5a6a 70%, ${tintColor}30 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Frost/snow particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${5 + (i * 4.5)}%`,
              top: `${10 + ((i * 17) % 70)}%`,
              width: `${4 + (i % 4) * 2}px`,
              height: `${4 + (i % 4) * 2}px`,
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '50%',
              boxShadow: '0 0 15px 5px rgba(255,255,255,0.08)',
            }}
          />
        ))}
        {/* Ice crystal shapes */}
        <svg className="absolute w-full h-full opacity-10" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <polygon points="200,100 220,150 200,200 180,150" fill="white" />
          <polygon points="800,300 830,370 800,440 770,370" fill="white" />
          <polygon points="150,500 175,560 150,620 125,560" fill={accentColor} />
          <polygon points="700,150 720,200 700,250 680,200" fill={accentColor} />
        </svg>
        {/* Cold mist */}
        <div
          className="absolute"
          style={{
            width: '100%',
            height: '300px',
            bottom: '0',
            background: `linear-gradient(180deg, transparent 0%, rgba(200,220,240,0.08) 100%)`,
          }}
        />
      </div>
    ),

    // Modern Rebel - Bold urban street
    'modern-rebel': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Dark urban gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, #1a0a0a 0%, ${tintColor}60 30%, #2a1a1a 60%, ${tintColor}40 100%)`,
            opacity: 0.6,
          }}
        />
        {/* Bold geometric shapes */}
        <svg className="absolute w-full h-full" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="rebelGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0.25" />
              <stop offset="100%" stopColor={accentColor} stopOpacity="0.05" />
            </linearGradient>
          </defs>
          {/* Angular shapes */}
          <polygon points="0,0 200,0 100,150" fill="url(#rebelGrad)" />
          <polygon points="800,0 1000,0 1000,200 900,100" fill="url(#rebelGrad)" />
          <polygon points="0,600 150,500 100,800 0,800" fill={accentColor} opacity="0.15" />
          <polygon points="850,800 1000,600 1000,800" fill={accentColor} opacity="0.2" />
          <rect x="700" y="250" width="120" height="120" fill={accentColor} opacity="0.1" transform="rotate(15 760 310)" />
          <rect x="150" y="350" width="80" height="80" fill={tintColor} opacity="0.15" transform="rotate(-10 190 390)" />
        </svg>
        {/* Red accent glow */}
        <div
          className="absolute"
          style={{
            width: '400px',
            height: '400px',
            left: '-100px',
            bottom: '-100px',
            background: `radial-gradient(circle, ${accentColor}25 0%, transparent 50%)`,
            filter: 'blur(30px)',
          }}
        />
      </div>
    ),

    // Ann Taylor - Classic elegant sophistication
    'ann-taylor': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Professional navy gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(160deg, ${tintColor}50 0%, #2a3a4a 40%, ${tintColor}40 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Elegant flowing curves */}
        <svg className="absolute w-full h-full opacity-15" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <path d="M-100,250 C200,200 400,280 700,230 S1000,280 1200,250" stroke={accentColor} strokeWidth="2" fill="none" />
          <path d="M-100,350 C250,300 500,380 750,330 S1000,370 1200,350" stroke={accentColor} strokeWidth="1" fill="none" />
          <ellipse cx="800" cy="180" rx="150" ry="80" fill={tintColor} opacity="0.3" />
          <ellipse cx="150" cy="550" rx="120" ry="60" fill={accentColor} opacity="0.2" />
        </svg>
      </div>
    ),

    // Tahari - Elegant gold warmth
    'tahari': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Warm gold gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(145deg, ${tintColor}50 0%, #4a3a25 35%, ${tintColor}40 70%, #3a2a1a 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Gold shimmer shapes */}
        <svg className="absolute w-full h-full" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <radialGradient id="goldGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={glowColor} stopOpacity="0.2" />
              <stop offset="100%" stopColor={glowColor} stopOpacity="0" />
            </radialGradient>
          </defs>
          <ellipse cx="250" cy="300" rx="200" ry="150" fill="url(#goldGrad)" />
          <ellipse cx="700" cy="450" rx="180" ry="130" fill="url(#goldGrad)" />
          <ellipse cx="500" cy="200" rx="150" ry="100" fill={accentColor} opacity="0.08" />
        </svg>
      </div>
    ),

    // Nicole Miller - Sophisticated bronze warmth
    'nicole-miller': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Warm bronze gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(150deg, ${tintColor}50 0%, #5a4538 40%, ${tintColor}35 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Soft abstract shapes */}
        <svg className="absolute w-full h-full opacity-15" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <circle cx="200" cy="250" r="180" fill={glowColor} opacity="0.3" />
          <circle cx="750" cy="500" r="150" fill={accentColor} opacity="0.25" />
          <ellipse cx="500" cy="700" rx="300" ry="100" fill={tintColor} opacity="0.2" />
        </svg>
      </div>
    ),

    // Taryn Rose - Soft blush comfort
    'taryn-rose': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Soft rose gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(160deg, ${tintColor}45 0%, #5a3a38 40%, ${tintColor}35 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Petal-like shapes */}
        <svg className="absolute w-full h-full opacity-12" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <ellipse cx="180" cy="350" rx="100" ry="180" fill={glowColor} transform="rotate(-25 180 350)" />
          <ellipse cx="800" cy="280" rx="80" ry="150" fill={accentColor} transform="rotate(20 800 280)" />
          <ellipse cx="500" cy="600" rx="120" ry="200" fill={glowColor} transform="rotate(-10 500 600)" opacity="0.5" />
          <circle cx="300" cy="150" r="80" fill={accentColor} opacity="0.3" />
        </svg>
      </div>
    ),

    // Jason Wu - Clean modern minimal
    'jason-wu': (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Cool slate gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(170deg, ${tintColor}45 0%, #2a3a48 45%, ${tintColor}35 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Minimal geometric lines */}
        <svg className="absolute w-full h-full opacity-10" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
          <line x1="100" y1="180" x2="400" y2="180" stroke={accentColor} strokeWidth="1" />
          <line x1="600" y1="500" x2="900" y2="500" stroke={accentColor} strokeWidth="1" />
          <rect x="750" y="200" width="100" height="100" stroke={accentColor} strokeWidth="1" fill="none" />
          <rect x="100" y="450" width="80" height="80" stroke={tintColor} strokeWidth="1" fill="none" />
          <line x1="0" y1="350" x2="200" y2="350" stroke={accentColor} strokeWidth="0.5" />
        </svg>
      </div>
    ),
  };

  return atmosphereStyles[slug] || null;
};

// ============================================
// CONTENT CARD COMPONENT
// ============================================
const ContentCard = ({ icon: Icon, title, content, accentColor, tintColor, delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  const [isHovered, setIsHovered] = useState(false);
  const prefersReduced = usePrefersReducedMotion();

  const softAccent = accentColor ? `${accentColor}15` : 'rgba(255,255,255,0.03)';
  const tint = tintColor || '#1a1a1a';

  return (
    <div
      ref={ref}
      className="p-8 rounded-2xl relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: `linear-gradient(145deg, ${tint}50 0%, ${tint}35 50%, ${tint}45 100%)`,
        borderTop: `1px solid ${isHovered ? accentColor + '50' : accentColor + '30'}`,
        borderLeft: `1px solid ${tint}70`,
        borderRight: `1px solid ${tint}40`,
        borderBottom: `1px solid ${tint}30`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? isHovered && !prefersReduced ? 'translateY(-4px)' : 'translateY(0)'
          : 'translateY(12px)',
        transition: `
          opacity ${DURATION.normal}ms ${EASING} ${delay}ms,
          transform ${DURATION.normal}ms ${EASING} ${delay}ms,
          border-color ${DURATION.fast}ms ${EASING},
          background ${DURATION.fast}ms ${EASING}
        `,
      }}
    >
      {/* Subtle brand glow on hover */}
      <div
        className="absolute inset-0 pointer-events-none rounded-2xl"
        style={{
          background: `radial-gradient(ellipse at top left, ${accentColor}12 0%, transparent 50%)`,
          opacity: isHovered ? 1 : 0,
          transition: `opacity ${DURATION.fast}ms ${EASING}`,
        }}
      />
      <div className="relative z-10">
        <div className="flex items-center gap-4 mb-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
              background: softAccent,
              border: `1px solid ${accentColor}20`,
              transform: isHovered && !prefersReduced ? 'scale(1.04) rotate(2deg)' : 'scale(1) rotate(0deg)',
              transition: `transform ${DURATION.fast}ms ${EASING}`,
            }}
          >
            <Icon size={20} style={{ color: accentColor || 'rgba(255,255,255,0.5)' }} />
          </div>
          <h3 className="text-white text-[17px] font-medium tracking-[-0.01em]">{title}</h3>
        </div>
        <p className="text-[#9a9a9a] text-[15px] leading-[1.75]">
          {content}
        </p>
      </div>
    </div>
  );
};

// ============================================
// FLOATING BRAND CARD COMPONENT
// ============================================
const FloatingBrandCard = ({ src, alt, glowColor, tintColor, animationStage }) => {
  const prefersReduced = usePrefersReducedMotion();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative"
      style={{
        width: '100%',
        maxWidth: '720px',
        aspectRatio: '4 / 5',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Simplified ambient glow - reduced blur for performance */}
      <div
        className="absolute rounded-[50px]"
        style={{
          inset: '-40px',
          background: `radial-gradient(ellipse 100% 100% at 50% 50%, ${glowColor}40 0%, transparent 60%)`,
          opacity: animationStage >= 1 ? 0.5 : 0,
          transition: `opacity ${DURATION.atmosphere}ms ${EASING_OUT}`,
        }}
      />

      {/* The card container with soft mask edges */}
      <div
        className="relative w-full h-full"
        style={{
          opacity: animationStage >= 2 ? 1 : 0,
          transform: animationStage >= 2
            ? isHovered && !prefersReduced
              ? 'translateY(0) scale(1.01)'
              : 'translateY(0) scale(1)'
            : 'translateY(12px) scale(0.98)',
          transition: `
            opacity ${DURATION.heroEntrance}ms ${EASING_OUT},
            transform ${DURATION.heroEntrance}ms ${EASING_OUT}
          `,
        }}
      >
        {/* Card with rounded corners */}
        <div
          className="relative w-full h-full rounded-[20px] overflow-hidden"
          style={{
            boxShadow: `
              0 25px 60px rgba(0,0,0,0.35),
              0 12px 30px rgba(0,0,0,0.25),
              0 0 80px ${glowColor}20
            `,
          }}
        >
          {/* Brand image */}
          <img
            src={src}
            alt={alt}
            className="w-full h-full"
            style={{ objectFit: 'cover' }}
          />
        </div>

        {/* Soft edge feathering - makes card blend into background */}
        <div
          className="absolute inset-0 pointer-events-none rounded-[20px]"
          style={{
            boxShadow: `
              inset 20px 0 30px -15px ${tintColor}60,
              inset -15px 0 25px -12px ${tintColor}40,
              inset 0 20px 30px -15px ${tintColor}30,
              inset 0 -25px 35px -15px ${tintColor}50
            `,
          }}
        />
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const BrandDetailPage = () => {
  const { slug } = useParams();
  const brand = getBrandBySlug(slug);
  const [animationStage, setAnimationStage] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const prefersReduced = usePrefersReducedMotion();
  const parallaxOffset = useParallax(0.012); // Very subtle: 12px per 1000px scroll

  // Staged animation sequence
  useEffect(() => {
    if (prefersReduced) {
      setAnimationStage(5);
      return;
    }

    const timers = [
      setTimeout(() => setAnimationStage(1), 100),   // Background atmosphere
      setTimeout(() => setAnimationStage(2), 350),   // Brand card
      setTimeout(() => setAnimationStage(3), 650),   // Text block
      setTimeout(() => setAnimationStage(4), 950),   // Chips
      setTimeout(() => setAnimationStage(5), 1150),  // Metadata
    ];

    return () => timers.forEach(clearTimeout);
  }, [prefersReduced, slug]);

  // Reset animation on brand change
  useEffect(() => {
    setAnimationStage(0);
    setImageLoaded(false);
  }, [slug]);

  // Preload brand image
  useEffect(() => {
    if (brand?.coverImage) {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.src = brand.coverImage;
    }
  }, [brand?.coverImage]);

  if (!brand) {
    return (
      <main className="bg-[#0a0a0a] min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-[1320px] mx-auto text-center">
          <h1 className="text-[48px] font-extralight text-white mb-6">Brand Not Found</h1>
          <p className="text-[#8a8a8a] text-[17px] mb-8">The brand you're looking for doesn't exist.</p>
          <Link
            to="/brands"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0a0a0a] rounded-full text-[15px] font-medium hover:bg-white/90 transition-all duration-300"
          >
            <ArrowLeft size={18} />
            Back to Brands
          </Link>
        </div>
      </main>
    );
  }

  const sectionIcons = {
    background: Lightbulb,
    vision: Eye,
    marketing: Megaphone,
    retailFit: Store,
  };

  const sectionTitles = {
    background: 'Background',
    vision: 'Vision',
    marketing: 'Marketing',
    retailFit: 'Retail Fit',
  };

  const accentColor = brand.accentColor || '#ffffff';
  const tintColor = brand.tintColor || '#1a1a1a';
  const glowColor = brand.glowColor || accentColor;

  return (
    <main className="min-h-screen relative overflow-hidden">
      {/* ============================================ */}
      {/* FULL PAGE BACKGROUND - Spans entire page */}
      {/* ============================================ */}

      {/* Base brand color */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(180deg, ${tintColor} 0%, ${tintColor}90 20%, ${tintColor}70 40%, ${tintColor}50 60%, ${tintColor}30 80%, ${tintColor}20 100%)`,
        }}
      />

      {/* Brand-specific atmospheric elements - FULL PAGE */}
      <div className="fixed inset-0 pointer-events-none">
        <BrandAtmosphere slug={slug} tintColor={tintColor} accentColor={accentColor} glowColor={glowColor} />
      </div>

      {/* Ambient glow layer */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 80% 60% at 20% 30%, ${glowColor}40 0%, transparent 50%),
            radial-gradient(ellipse 60% 50% at 80% 70%, ${accentColor}25 0%, transparent 45%)
          `,
          opacity: animationStage >= 1 ? 0.6 : 0,
          transition: `opacity ${DURATION.atmosphere}ms ${EASING_OUT}`,
        }}
      />

      {/* Subtle vignette for depth */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.4) 100%)`,
        }}
      />

      {/* Hero Section */}
      <section className="relative min-h-[88vh] flex items-center">

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1320px] mx-auto px-6 py-20 lg:py-24">
          {/* Back Button */}
          <Link
            to="/brands"
            className="inline-flex items-center gap-2 text-white/45 hover:text-white mb-12 group"
            style={{
              opacity: animationStage >= 3 ? 1 : 0,
              transform: animationStage >= 3 ? 'translateY(0)' : 'translateY(-8px)',
              transition: `all ${DURATION.normal}ms ${EASING}`,
            }}
          >
            <ArrowLeft
              size={15}
              className="transition-transform"
              style={{
                transform: 'translateX(0)',
                transition: `transform ${DURATION.fast}ms ${EASING}`,
              }}
            />
            <span className="text-[13px] tracking-wide group-hover:-translate-x-0">Back to Brands</span>
            <style>{`
              .group:hover svg { transform: translateX(-3px); }
            `}</style>
          </Link>

          {/* Main Hero Layout - Hero image takes ~50-55% on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-12 items-center">
            {/* Floating Brand Card - larger footprint */}
            <div className="flex justify-center lg:justify-start order-1 lg:-ml-4">
              <FloatingBrandCard
                src={brand.coverImage}
                alt={brand.name}
                glowColor={glowColor}
                tintColor={tintColor}
                animationStage={animationStage}
              />
            </div>

            {/* Brand Info Column - with subtle backdrop for readability */}
            <div
              className="flex flex-col justify-center order-2 relative"
              style={{
                padding: '2rem',
                marginRight: '-2rem',
                borderRadius: '24px',
                background: `linear-gradient(135deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.3) 50%, transparent 100%)`,
              }}
            >
              {/* Eyebrow */}
              <p
                className="text-[11px] mb-5 uppercase tracking-[0.2em] font-medium"
                style={{
                  color: accentColor,
                  opacity: animationStage >= 3 ? 1 : 0,
                  transform: animationStage >= 3 ? 'translateY(0)' : 'translateY(10px)',
                  transition: `all ${DURATION.normal}ms ${EASING}`,
                }}
              >
                {brand.hero.eyebrow}
              </p>

              {/* Brand Name */}
              <h1
                className="text-[40px] md:text-[50px] lg:text-[56px] font-extralight text-white mb-5 tracking-[-0.025em] leading-[1.05]"
                style={{
                  opacity: animationStage >= 3 ? 1 : 0,
                  transform: animationStage >= 3 ? 'translateY(0)' : 'translateY(10px)',
                  transition: `all ${DURATION.normal}ms ${EASING} 50ms`,
                }}
              >
                {brand.name}
              </h1>

              {/* Headline */}
              <h2
                className="text-white/70 text-[18px] md:text-[21px] font-light mb-6 leading-[1.45] max-w-[420px]"
                style={{
                  opacity: animationStage >= 3 ? 1 : 0,
                  transform: animationStage >= 3 ? 'translateY(0)' : 'translateY(10px)',
                  transition: `all ${DURATION.normal}ms ${EASING} 100ms`,
                }}
              >
                {brand.hero.headline}
              </h2>

              {/* Subhead */}
              <p
                className="text-[#777] text-[15px] leading-[1.8] mb-8 max-w-[400px]"
                style={{
                  opacity: animationStage >= 3 ? 1 : 0,
                  transform: animationStage >= 3 ? 'translateY(0)' : 'translateY(10px)',
                  transition: `all ${DURATION.normal}ms ${EASING} 150ms`,
                }}
              >
                {brand.hero.subhead}
              </p>

              {/* Badges */}
              <div
                className="flex flex-wrap gap-2.5 mb-8"
                style={{
                  opacity: animationStage >= 4 ? 1 : 0,
                  transform: animationStage >= 4 ? 'translateY(0)' : 'translateY(8px)',
                  transition: `all ${DURATION.normal}ms ${EASING}`,
                }}
              >
                {brand.hero.badges.slice(0, 2).map((badge, i) => (
                  <span
                    key={badge}
                    className="text-[10px] px-3.5 py-1.5 rounded-full font-medium uppercase tracking-[0.08em]"
                    style={{
                      color: accentColor,
                      background: `${accentColor}0a`,
                      border: `1px solid ${accentColor}18`,
                      opacity: animationStage >= 4 ? 1 : 0,
                      transition: `opacity ${DURATION.normal}ms ${EASING} ${i * STAGGER.tight}ms`,
                    }}
                  >
                    {badge}
                  </span>
                ))}
              </div>

              {/* Meta info */}
              <div
                className="flex flex-wrap gap-6 text-[13px] pt-6"
                style={{
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  opacity: animationStage >= 5 ? 1 : 0,
                  transform: animationStage >= 5 ? 'translateY(0)' : 'translateY(6px)',
                  transition: `all ${DURATION.normal}ms ${EASING}`,
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-white/28">Type</span>
                  <span className="text-white/55 capitalize">{brand.type === 'in_house' ? 'In-House' : 'Licensed'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white/28">Program</span>
                  <span className="text-white/55">{brand.genderProgram}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* Content Section - Transparent, lets full-page background show through */}
      <section className="px-6 pt-6 pb-20 relative">
        <div className="max-w-[1320px] mx-auto relative z-10">
          {/* Brand Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-20">
            {Object.entries(brand.sections).map(([key, content], index) => {
              const Icon = sectionIcons[key];
              const title = sectionTitles[key];
              return (
                <ContentCard
                  key={key}
                  icon={Icon}
                  title={title}
                  content={content}
                  accentColor={accentColor}
                  tintColor={tintColor}
                  delay={index * STAGGER.normal}
                />
              );
            })}
          </div>

          {/* CTA Section */}
          <CTASection brand={brand} accentColor={accentColor} tintColor={tintColor} glowColor={glowColor} />
        </div>
      </section>
    </main>
  );
};

// ============================================
// CTA SECTION COMPONENT
// ============================================
const CTASection = ({ brand, accentColor, tintColor, glowColor }) => {
  const [ref, isVisible] = useScrollReveal();
  const [isHovered, setIsHovered] = useState(false);
  const prefersReduced = usePrefersReducedMotion();

  return (
    <div
      ref={ref}
      className="p-10 rounded-2xl text-center relative overflow-hidden"
      style={{
        background: `linear-gradient(145deg, ${tintColor}55 0%, ${tintColor}40 50%, ${tintColor}50 100%)`,
        borderTop: `1px solid ${accentColor}40`,
        borderLeft: `1px solid ${tintColor}70`,
        borderRight: `1px solid ${tintColor}40`,
        borderBottom: `1px solid ${tintColor}30`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
        transition: `all ${DURATION.normal}ms ${EASING}`,
      }}
    >
      {/* Ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, ${glowColor}15 0%, transparent 60%)`,
        }}
      />
      <div className="relative z-10">
        <h3 className="text-white text-[22px] font-light mb-4 tracking-[-0.01em]">
          {brand.cta.title}
        </h3>
        <p className="text-[#999] text-[15px] mb-7 max-w-[460px] mx-auto leading-[1.7]">
          {brand.cta.body}
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-medium"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            background: accentColor,
            color: '#0a0a0a',
            boxShadow: isHovered
              ? `0 8px 32px ${accentColor}50, 0 0 60px ${glowColor}30`
              : `0 4px 20px ${accentColor}30`,
            transform: isHovered && !prefersReduced ? 'translateY(-2px)' : 'translateY(0)',
            transition: `all ${DURATION.fast}ms ${EASING}`,
          }}
        >
          {brand.cta.buttonText}
        </Link>
      </div>
    </div>
  );
};

export default BrandDetailPage;
